// src/services/NotificationService.ts
import { onMessage, getToken } from "firebase/messaging";
import { messaging, firebaseConfig } from "../config/firebase";
import { toast } from "react-hot-toast";
import TokenManager from "./TokenManager";
import logger from "../utils/logger";

export class NotificationService {
  private static instance: NotificationService;
  private messageUnsubscribe: (() => void) | null = null;
  private tokenManager: TokenManager;

  private constructor() {
    this.tokenManager = TokenManager.getInstance();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  public async initialize(): Promise<void> {
    try {
      logger.log("🔔 NotificationService: Starting initialization...");
      logger.log("🔔 NotificationService: Step 1 - Registering service worker");
      await this.registerServiceWorker();
      logger.log("🔔 NotificationService: Step 2 - Initializing FCM token");
      await this.initializeToken();
      logger.log("🔔 NotificationService: Step 3 - Setting up message listener");
      await this.setupMessageListener();
      logger.log("🔔 NotificationService: ✅ Initialization complete!");
    } catch (error) {
      logger.error("❌ NotificationService: Initialization failed:", error);
      throw error;
    }
  }

  private async initializeToken(): Promise<void> {
    try {
      await this.tokenManager.getValidToken();
    } catch (error) {
      logger.error("Failed to initialize FCM token:", error);
      throw error;
    }
  }

  private async registerServiceWorker(): Promise<void> {
    logger.log("🔔 SW: Checking service worker support...");
    if ("serviceWorker" in navigator) {
      try {
        logger.log("🔔 SW: Service worker supported, registering...");
        const configString = encodeURIComponent(JSON.stringify(firebaseConfig));
        logger.log("🔔 SW: Firebase config encoded:", configString.substring(0, 50) + "...");
        
        const registration = await navigator.serviceWorker.register(
          `/firebase-messaging-sw.js?config=${configString}`, 
          { scope: "/" }
        );
        logger.log("🔔 SW: Service worker registered:", registration);

        // Wait for the service worker to be ready
        if (!registration.active) {
          logger.log("🔔 SW: Waiting for service worker to activate...");
          await new Promise<void>((resolve) => {
            registration.addEventListener('activate', () => {
              logger.log("🔔 SW: Service worker activated!");
              resolve();
            }, { once: true });
          });
        } else {
          logger.log("🔔 SW: Service worker already active");
        }

        // Send INIT_MESSAGING message to service worker to enable background messaging
        if (registration.active) {
          logger.log("🔔 SW: Sending INIT_MESSAGING to service worker...");
          registration.active.postMessage({ type: 'INIT_MESSAGING' });
          logger.log("🔔 SW: ✅ INIT_MESSAGING sent successfully");
        }
      } catch (error) {
        logger.error("❌ SW: Service worker registration failed:", error);
        throw error;
      }
    } else {
      logger.warn("⚠️ SW: Service worker not supported in this browser");
    }
  }

  private async setupMessageListener(): Promise<void> {
    logger.log("🔔 MSG: Setting up message listener...");
    if (!messaging) {
      logger.error("❌ MSG: Firebase messaging not available");
      return;
    }

    if (this.messageUnsubscribe) {
      logger.log("🔔 MSG: Unsubscribing from previous listener");
      this.messageUnsubscribe();
    }

    logger.log("🔔 MSG: Creating foreground message listener");
    this.messageUnsubscribe = onMessage(messaging, (payload) => {
      logger.log("🔔 MSG: Foreground message received:", payload);
      logger.log("🔔 MSG: Document visibility:", document.visibilityState);
      
      if (document.visibilityState === "visible" && payload.notification) {
        const { title, body } = payload.notification;
        logger.log("🔔 MSG: Showing toast notification:", { title, body });
        toast(body || "", {
          duration: 4000,
          id: Date.now().toString(),
        });
      } else if (document.visibilityState !== "visible") {
        logger.log("🔔 MSG: Document not visible, background handling should take over");
      }
    });
    logger.log("🔔 MSG: ✅ Message listener setup complete");
  }
}

export default NotificationService;